'use client';

import LoadingButton from '@mui/lab/LoadingButton';
import {
  AppBar,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Drawer,
  Slide,
  Toolbar,
} from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isEmpty } from 'lodash';
import { useParams } from 'next/navigation';
import { TransitionProps } from 'notistack';
import { useQueryState } from 'nuqs';
import { forwardRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useGetEvent } from 'src/api/events';
import EventPriceCard from 'src/components/event-price-card/event-price-card';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import { useResponsive } from 'src/hooks/use-responsive';
import { useTranslate } from 'src/locales';
import { CartItemDTO, CartShopItemDTO, EventType } from 'src/types';

import ShopItemsList from './event-shop-list';
import { getButtonLabel } from './event-utils';
import IncrementerButton from './Increment-button';
import TicketAvailable from './ticket-available';
import TicketInfoHeader from './ticket-info-header';
import { useTicketCalculations } from './useTicketCalculations';

type EventCartModalProps = {
  onSubmit: () => void;
  flatFee?: number;
};
const Transition = forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>
  ) => <Slide direction="up" ref={ref} {...props} />
);

export default function EventCartModal({ onSubmit, flatFee }: EventCartModalProps) {
  const mdUp = useResponsive('up', 'md');
  const { t } = useTranslate('common');

  const { t: tEvent } = useTranslate('event');
  const { id } = useParams<{ id: string }>();
  const methods = useFormContext();
  const [checkout, setCheckout] = useQueryState('checkout');
  const { data: event, isLoading } = useGetEvent(id);

  const {
    watch,
    control,
    getValues,
    formState: { isSubmitting },
  } = methods;

  const tickets = watch('tickets') as CartItemDTO[];
  const shopItemsList = getValues('shopItems') as CartShopItemDTO[];

  const handleClose = () => {
    setCheckout(null);
  };

  const {
    serviceFees,
    promoCode,
    discountedTotalCost,
    isFreeTicket,
    appliedDiscount,
    hasTicketWithQuantityMoreThanZero,
    hasShopItemWithQuantityMoreThanZero,
    handleIncrease,
    handleDecrease,
    handleShopIncrease,
    handleShopDecrease,
  } = useTicketCalculations({
    tickets,
    shopItemsList,
    getValues,
    flatFee,
    eventId: Number(id),
  });

  const subTotal = (
    <Stack spacing={1} sx={{ mb: 20, px: 2 }}>
      <Divider sx={{ width: '100%', mb: 1 }} />
      <Typography variant="h4">{tEvent('cart.breakdown')}</Typography>
      <EventPriceCard
        appliedPromoCode={promoCode}
        fees={serviceFees}
        isFreeTicket={isFreeTicket}
        total={discountedTotalCost}
        currency={event?.currency}
        discount={appliedDiscount}
      />
    </Stack>
  );

  const renderTicket =
    isEmpty(tickets) && isEmpty(shopItemsList) ? (
      <Stack gap={2} px={4} pt={6} alignItems="center" justifyContent="center">
        <Iconify icon="bi:cart-x" width={25} height={25} />
        <Typography color="#c7c7c7">{tEvent('cart.empty-cart')}</Typography>
      </Stack>
    ) : (
      <Stack gap={2} px={2} pt={2}>
        {tickets?.map(
          (ticket, index) =>
            ticket.quantityInCart > 0 && (
              <TicketAvailable
                key={index}
                ticket={ticket}
                index={index}
                control={control}
                handleIncrease={handleIncrease}
                handleDecrease={handleDecrease}
              />
            )
        )}
        {shopItemsList &&
          shopItemsList.map(
            (item, index) =>
              item &&
              item.quantityInCart > 0 && (
                <Stack
                  key={index}
                  direction="column"
                  sx={{ backgroundColor: '#000', px: 2, py: 2, borderRadius: 2 }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    pb={2}
                    gap={1}
                  >
                    <Stack sx={{ maxWidth: '80%' }}>
                      <TicketInfoHeader ticket={item} />
                    </Stack>
                    <Image
                      src={item?.picture?.url}
                      sx={{ borderRadius: 1 }}
                      width="6.5rem"
                      height="4.5rem"
                    />
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body2">{item.net}$</Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-end"
                      gap={2}
                      sx={{ width: '80%' }}
                    >
                      <Controller
                        name={`shopItems.${index}.quantityInCart`}
                        control={control}
                        render={({ field }) => (
                          <IncrementerButton
                            quantity={field.value}
                            onIncrease={() => handleShopIncrease(field, index)}
                            onDecrease={() => handleShopDecrease(field, index)}
                            disabledDecrease={field.value <= 0}
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              )
          )}
      </Stack>
    );

  const renderForm = (
    <Stack
      spacing={2.5}
      component="div"
      sx={{
        p: 2,
        pb: 4,
        position: 'absolute',
        left: 0,
        bottom: 0,
        backgroundColor: '#000',
        width: '100%',
      }}
    >
      <LoadingButton
        fullWidth
        color="inherit"
        size="large"
        loading={isSubmitting}
        type="submit"
        onClick={(e) => {
          e.preventDefault(); // new line added

          console.log('subitting ');
          onSubmit();
          // your further logic
        }}
        variant="contained"
      >
        {getButtonLabel({
          hasPendingRequest: event?.hasPendingRequest,
          type: event?.type ?? EventType.PUBLIC,
          hasShopItem: !isEmpty(shopItemsList),
          isNotCheckoutModal: false,
          t,
        })}
      </LoadingButton>
    </Stack>
  );

  return mdUp ? (
    <Drawer
      anchor="right"
      open={Boolean(checkout)}
      onClose={() => handleClose()}
      sx={{
        '& .MuiDrawer-paper': {
          boxShadow: 'none',
          width: '40vw',
          height: '100%',
          p: 3,
          '&::-webkit-scrollbar': {
            display: 'none', // Hide the scrollbar
          },
        },
      }}
    >
      <Stack
        maxHeight="100vh"
        sx={{
          overflow: 'scroll',
          '&::-webkit-scrollbar': {
            display: 'none', // Hide the scrollbar
          },
        }}
      >
        {renderTicket}

        {isEmpty(shopItemsList) ? null : (
          <Stack py={2} mt={2} gap={1}>
            <Stack px={2}>
              <Typography variant="h5">Add-ons</Typography>
            </Stack>
            <ShopItemsList
              control={control}
              shopItems={shopItemsList}
              handleShopIncrease={handleShopIncrease}
            />
          </Stack>
        )}
        {(hasTicketWithQuantityMoreThanZero || hasShopItemWithQuantityMoreThanZero) && subTotal}
        {renderForm}
      </Stack>
    </Drawer>
  ) : (
    <Dialog
      fullScreen
      open={Boolean(checkout)}
      onClose={() => handleClose()}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar sx={{ justifyContent: 'space-between', px: 2 }}>
          <Typography variant="h6">Cart</Typography>
          <Button variant="outlined" onClick={handleClose}>
            Close
          </Button>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ p: 0 }}>
        {renderTicket}
        {isEmpty(shopItemsList) ? null : (
          <Stack py={2} mt={2} gap={1}>
            <Stack px={2}>
              <Typography variant="h5">Add-ons</Typography>
            </Stack>
            <ShopItemsList
              control={control}
              shopItems={shopItemsList}
              handleShopIncrease={handleShopIncrease}
            />
          </Stack>
        )}
        {(hasTicketWithQuantityMoreThanZero || hasShopItemWithQuantityMoreThanZero) && subTotal}
        {renderForm}
      </DialogContent>
    </Dialog>
  );
}
